import {
  ChevronDownIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  AdsGroupAd,
  KeywordConstraint,
  MarketingApproach,
  marketingApproaches,
} from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/Autocomplete';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { formFieldUrlWithParamsValidator } from '@superfunnel/utils';
import { httpsCallable } from 'firebase/functions';
import Lottie from 'lottie-react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { uid } from 'uid';
import { functions } from '../Firebase';
import languages from '../assets/languages.json';
import KeywordsAutocomplete from '../components/KeywordsAutocomplete';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { useHasFeature } from '../hooks/useHasFeature';
import { suggestCampaignForAd } from '../services/DynamicPagesService';
import { camelCaseToTitle, classNames } from '../utils';
import CreateFormModal from './CreateFormModal';

interface DynamicPageData {
  name?: string;
  path?: string;
  goal?: string;
  audience?: string;
  description?: string;
  appStoreLink?: string;
  playStoreLink?: string;
  link?: string;
  calendlyLink?: string;
  formId?: string;
  extraLink?: string;
  extraText?: string;
  language?: { name: string; code: string };
  domains?: string[];
  variants?: {
    id: string;
    name?: string;
    prompt?: string;
    keywords?: KeywordConstraint[];
    marketingApproach?: MarketingApproach;
  }[];
}

interface Props {
  adAccountsAd?: AdsGroupAd & { id: string };
  open: boolean;
  onClose: () => void;
}

export default function CreateDynamicPageModal({
  adAccountsAd,
  open,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const { show } = useContext(SnackbarContext);
  const { slug } = useParams();

  const { selectedAccount } = useContext(UserContext);
  const { forms, domains, primaryDomain } = useContext(DashboardContext);

  const [websiteBuildingAnimation, setWebsiteBuildingAnimation] =
    useState<any>(null);
  const [dataFromAd, setDataFromAd] = useState<Partial<DynamicPageData>>();
  const [loadingDataForAd, setLoadingDataForAd] = useState(false);
  const [dataForAdError, setDataForAdError] = useState<Error>();

  useEffect(() => {
    import('../assets/lotties/website-building.json').then((module) =>
      setWebsiteBuildingAnimation(module.default),
    );
  }, []);
  const [data, setData] = useState<DynamicPageData>({ path: uid(6) });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error>();
  const [createFormDialogOpen, setCreateFormDialogOpen] = useState(false);

  const campaignGoal = useMemo(
    () => data.goal ?? dataFromAd?.goal,
    [data, dataFromAd],
  );

  useEffect(() => {
    if (open) return;

    setData({ path: uid(6) });
    setCreating(false);
    setError(undefined);
    setDataFromAd(undefined);
    setLoadingDataForAd(false);
    setDataForAdError(undefined);
  }, [open]);

  const loadDataFromAd = useCallback(() => {
    if (!adAccountsAd || !selectedAccount) {
      setDataFromAd(undefined);
      return;
    }

    setLoadingDataForAd(true);
    setDataForAdError(undefined);
    suggestCampaignForAd(selectedAccount.id, adAccountsAd.id)
      .then((res) => {
        setDataFromAd({
          name: res.campaign.name,
          path: res.campaign.path,
          goal: res.campaign.goal,
          audience: res.campaign.audience,
          description: res.campaign.description,
          variants: res.variants.map((v) => ({ ...v, id: uid(8) })),
        });
      })
      .catch((e) => {
        setDataForAdError(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoadingDataForAd(false);
      });
  }, [selectedAccount, adAccountsAd]);

  useEffect(() => {
    if (dataFromAd) return;

    loadDataFromAd();
  }, [loadDataFromAd, dataFromAd]);

  const createCampaign = useCallback(async () => {
    if (creating) return;
    if (!primaryDomain) return;

    if (data.domains && !data.domains?.length) {
      show('Please select at least one domain');
      return;
    }

    setCreating(true);

    try {
      const fn = httpsCallable<any, any>(
        functions,
        'accounts/campaigns/createCampaign',
      );

      const res = await fn({
        ...dataFromAd,
        ...data,
        domains: data.domains ?? [primaryDomain.data().domain],
        accountId: selectedAccount?.id,
        adId: adAccountsAd?.id,
      });
      // await fetchCampaigns(firebaseUser?.uid ?? '');

      navigate(`/workspace/${slug}/pages/${res.data.campaignId}/variants`);
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setCreating(false);
  }, [
    creating,
    primaryDomain,
    data,
    show,
    dataFromAd,
    selectedAccount?.id,
    adAccountsAd?.id,
    navigate,
    slug,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createCampaign(),
      },
    ],
    [creating, onClose, createCampaign],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actionsAlignment={adAccountsAd ? 'end' : 'stretch'}
      actions={actions}
      width={adAccountsAd ? '6xl' : 'sm'}
      title="Create a new dynamic page"
    >
      {(loadingDataForAd || dataForAdError) && !dataFromAd ? (
        <div
          className={
            'absolute top-0 right-0 bottom-0 left-0 flex h-full flex-col items-center justify-center gap-8'
          }
        >
          {loadingDataForAd ? (
            <div className="w-full max-w-md">
              {websiteBuildingAnimation && (
                <Lottie animationData={websiteBuildingAnimation} loop={true} />
              )}
            </div>
          ) : (
            <></>
          )}
          {!loadingDataForAd && dataForAdError ? (
            <>
              <p className="mt-5 w-full text-center text-red-600">
                {dataForAdError.message}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadDataFromAd()}
              >
                Try Again
              </Button>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      <div
        className={classNames(
          adAccountsAd && !dataFromAd ? 'pointer-events-none invisible' : '',
          'flex flex-col justify-stretch gap-4 sm:flex-row',
        )}
      >
        <div className="mt-4 flex flex-1 flex-col gap-4">
          <Input
            title="Page name"
            readOnly={creating}
            value={data.name ?? dataFromAd?.name ?? ''}
            onChange={(e) => {
              setData((d) => {
                d.name = e.target.value;
                return { ...d };
              });
            }}
          />
          <Input
            title="Path"
            readOnly={creating}
            value={data.path ?? dataFromAd?.path}
            onChange={(e) => {
              setData((d) => {
                d.path = e.target.value;
                return { ...d };
              });
            }}
          />
          <div className="flex flex-col justify-start gap-1">
            <FieldTitle title="Page goal" />
            <Select
              value={data.goal ?? dataFromAd?.goal}
              disabled={creating}
              onValueChange={(e: string) => {
                setData((d) => {
                  d.goal = e;
                  return { ...d };
                });
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="select your goal..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leads">Collect leads</SelectItem>
                <SelectItem value="appDownload">App download</SelectItem>
                <SelectItem value="linkClick">Link click</SelectItem>
                <SelectItem value="calendly">Meeting via Calendly</SelectItem>
                <SelectItem value="assetDownload" disabled>
                  Asset download
                </SelectItem>
                <SelectItem value="demoRequest" disabled>
                  Demo request
                </SelectItem>
                <SelectItem value="ecourseRegistration" disabled>
                  ECourse registration
                </SelectItem>
                <SelectItem value="enrollment" disabled>
                  Enrollment application
                </SelectItem>
                <SelectItem value="eventRegistration" disabled>
                  Event registration
                </SelectItem>
                <SelectItem value="productSale" disabled>
                  Product sales
                </SelectItem>
                <SelectItem value="scheduleAppointment" disabled>
                  Schedule appointment
                </SelectItem>
                <SelectItem value="signup" disabled>
                  Signup
                </SelectItem>
                <SelectItem value="webinar" disabled>
                  Webinar registration
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {campaignGoal === 'appDownload' ? (
            <>
              <Input
                title="App Store link"
                type="url"
                readOnly={creating}
                value={data.appStoreLink ?? dataFromAd?.appStoreLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.appStoreLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(
                  data.appStoreLink ?? dataFromAd?.appStoreLink ?? '',
                )}
              />
              <Input
                title="Play Store link"
                type="url"
                readOnly={creating}
                value={data.playStoreLink ?? dataFromAd?.playStoreLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.playStoreLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(
                  data.playStoreLink ?? dataFromAd?.playStoreLink ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'linkClick' ? (
            <>
              <Input
                title="Link"
                type="url"
                readOnly={creating}
                value={data.link ?? dataFromAd?.link ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.link = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(
                  data.link ?? dataFromAd?.link ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'calendly' ? (
            <>
              <Input
                title="Calendly link"
                type="url"
                readOnly={creating}
                value={data.calendlyLink ?? dataFromAd?.calendlyLink ?? ''}
                onChange={(e) => {
                  setData((d) => {
                    d.calendlyLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(
                  data.calendlyLink ?? dataFromAd?.calendlyLink ?? '',
                )}
              />
            </>
          ) : (
            <></>
          )}
          {campaignGoal === 'leads' ? (
            <div className="flex flex-col justify-start gap-1">
              <FieldTitle title="Lead form" />
              <Select
                value={data.formId ?? dataFromAd?.formId ?? ''}
                disabled={creating || !forms}
                onValueChange={(e: string) => {
                  if (e === 'new') {
                    setCreateFormDialogOpen(true);
                    return;
                  }
                  setData((d) => {
                    d.formId = e;
                    return { ...d };
                  });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a lead form.." />
                </SelectTrigger>
                <SelectContent>
                  {forms?.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.data().name}
                    </SelectItem>
                  ))}
                  <SelectItem value="new">Create new...</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <></>
          )}
          <Input
            title="Audience"
            readOnly={creating}
            value={data.audience ?? dataFromAd?.audience ?? ''}
            onChange={(e) => {
              setData((d) => {
                d.audience = e.target.value;
                return { ...d };
              });
            }}
          />
          <div>
            <TextArea
              title="Page offering"
              readOnly={creating}
              value={data.description ?? dataFromAd?.description ?? ''}
              onChange={(e) => {
                setData((d) => {
                  d.description = e.target.value;
                  return { ...d };
                });
              }}
              rows={4}
              name="comment"
              id="comment"
            />
          </div>
          {primaryDomain?.data().type === 'custom' ? (
            <ChipsAutocomplete<string>
              title="Domains"
              selected={data.domains ?? [primaryDomain.data().domain]}
              loading={!domains}
              options={(domains ?? []).map((d) => d.data().domain)}
              getValue={(d) => d}
              getOptionId={(d) => d}
              onChange={(selected) => {
                setData((d) => {
                  d.domains = selected;
                  return { ...d };
                });
              }}
            />
          ) : (
            <></>
          )}
          <AdvancedSettings
            creating={creating}
            data={data}
            dataFromAd={dataFromAd}
            setData={setData}
          />
        </div>

        {adAccountsAd ? (
          <>
            <div></div>
            <CampaignVariants
              creating={creating}
              adGroupId={adAccountsAd.adGroupId}
              data={data}
              dataFromAd={dataFromAd}
              setData={setData}
            />
          </>
        ) : (
          <></>
        )}
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
      <CreateFormModal
        open={createFormDialogOpen}
        onClose={() => {
          setCreateFormDialogOpen(false);
        }}
      />
    </Modal>
  );
}

function AdvancedSettings({
  creating,
  data,
  setData,
}: {
  creating: boolean;
  data: DynamicPageData;
  dataFromAd?: DynamicPageData;
  setData: Dispatch<SetStateAction<DynamicPageData>>;
}) {
  const hasExtraLinkFeature = useHasFeature(
    'dynamicPages:create:fields:extraLink',
  );

  return (
    <Collapsible className="group">
      <CollapsibleTrigger className="flex items-center gap-1 py-2">
        <Cog6ToothIcon className="h-4 w-4" />
        Advanced Settings
        <div className="flex-1" />
        <ChevronDownIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-2">
          <h3>Settings</h3>
          <Autocomplete
            title="Language"
            selected={
              data?.language ??
              languages.find((l) => l.code === 'en-US') ??
              languages[0]
            }
            options={languages}
            getValue={(o) => o.name}
            getOptionId={(o) => o.code}
            onChange={(selected) => {
              setData((d: any) => {
                d.language = selected;
                return { ...d };
              });
            }}
          />
          {hasExtraLinkFeature && (
            <>
              <h3>Extra Information</h3>
              <Input
                placeholder="link"
                type="url"
                readOnly={creating}
                value={data.extraLink}
                onChange={(e) => {
                  setData((d: any) => {
                    d.extraLink = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(
                  data.extraLink ?? '',
                )}
              />
            </>
          )}
          <TextArea
            id="extraText"
            name="extraText"
            placeholder="content"
            rows={4}
            value={data.extraText}
            onChange={(e) => {
              setData((d: any) => {
                d.extraText = e.target.value;
                return { ...d };
              });
            }}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

type VariantData = NonNullable<DynamicPageData['variants']>[number];

function CampaignVariants({
  creating,
  adGroupId,
  data,
  dataFromAd,
  setData,
}: {
  creating: boolean;
  adGroupId: string;
  data: DynamicPageData;
  dataFromAd?: DynamicPageData;
  setData: Dispatch<SetStateAction<DynamicPageData>>;
}) {
  const variants = data.variants ?? dataFromAd?.variants ?? [];

  const keywordsFilter = useMemo(() => ({ adGroupId }), [adGroupId]);

  const update = useCallback(
    (newVariant: VariantData) => {
      setData((d) => {
        const newVariants = [...(d.variants ?? dataFromAd?.variants ?? [])];
        const existingIndex = newVariants.findIndex(
          (v) => v.id === newVariant.id,
        );
        if (existingIndex !== -1) {
          newVariants[existingIndex] = newVariant;
        } else {
          newVariants.push(newVariant);
        }

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  const duplicate = useCallback(
    (variant: VariantData) => {
      setData((d) => {
        const newVariants = [...(d.variants ?? dataFromAd?.variants ?? [])];
        const existingIndex = newVariants.findIndex((v) => v.id === variant.id);

        const newVariant = {
          ...variant,
          id: uid(8),
          name: [variant.name, 'Copy'].filter((x) => !!x).join(' '),
        };
        if (existingIndex !== -1) {
          newVariants.splice(existingIndex + 1, 0, newVariant);
        } else {
          newVariants.push(newVariant);
        }

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  const deleteVariant = useCallback(
    (variantId: string) => {
      setData((d) => {
        const newVariants = [
          ...(d.variants ?? dataFromAd?.variants ?? []),
        ].filter((v) => v.id !== variantId);

        return { ...d, variants: newVariants };
      });
    },
    [setData, dataFromAd],
  );

  return (
    <div className="mt-4 flex flex-1 flex-col divide-y">
      <label
        htmlFor="comment"
        className="mb-2 block text-sm leading-6 font-medium text-gray-900"
      >
        Suggested variants
      </label>

      {variants.map((variant, i) => {
        const setData = (_update: (variant: VariantData) => VariantData) => {
          update(_update(variant));
        };
        return (
          <Collapsible key={variant.id} className="group">
            <CollapsibleTrigger className="flex w-full items-center gap-1 py-3 hover:bg-gray-50">
              <div className="flex flex-col items-start gap-2">
                <span className="text-sm">
                  #{i + 1} {variant.name || 'Variant'}
                </span>

                {
                  // eslint-disable-next-line no-constant-condition
                  false && variant.keywords?.length ? (
                    <div className="flex flex-wrap gap-2 group-data-[state=open]:hidden">
                      {variant.keywords?.slice(0, 3).map((keyword) => (
                        <Badge
                          key={keyword.keyword}
                          variant="outline"
                          size="sm"
                        >
                          {keyword.keyword}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <></>
                  )
                }
              </div>

              <div className="flex-1" />

              <Button
                size="icon"
                variant="ghost"
                className="-my-2 group-hover:block group-data-[state=closed]:hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicate(variant);
                }}
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                className="-my-2 group-hover:block group-data-[state=closed]:hidden"
                color="negative"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteVariant(variant.id);
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>

              <Button size="icon" variant="ghost" className="-my-2">
                <ChevronDownIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="flex flex-col gap-2 py-2">
                <Input
                  title="Name"
                  readOnly={creating}
                  value={variant.name}
                  onChange={(e) => {
                    setData((d) => {
                      d.name = e.target.value;
                      return { ...d };
                    });
                  }}
                />
                <div className="flex flex-col justify-start gap-1">
                  <FieldTitle title="Marketing approach" />
                  <Select
                    value={variant.marketingApproach}
                    disabled={creating}
                    onValueChange={(e: MarketingApproach) => {
                      setData((d) => {
                        d.marketingApproach = e;
                        return { ...d };
                      });
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="select your approach..." />
                    </SelectTrigger>
                    <SelectContent>
                      {marketingApproaches.map((approach) => (
                        <SelectItem key={approach} value={approach}>
                          {camelCaseToTitle(approach)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <KeywordsAutocomplete
                  filter={keywordsFilter}
                  selected={variant.keywords || []}
                  onChange={(newKeywords) => {
                    setData((d) => {
                      d.keywords = newKeywords;
                      return { ...d };
                    });
                  }}
                />
                <TextArea
                  id="prompt"
                  name="prompt"
                  title="Prompt (optional)"
                  placeholder="Optional constraints for the variant creation"
                  rows={4}
                  value={variant.prompt}
                  onChange={(e) => {
                    setData((d) => {
                      d.prompt = e.target.value;
                      return { ...d };
                    });
                  }}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
      <div className="flex justify-end pt-3">
        <Button
          variant="ghost"
          size="micro"
          onClick={() => {
            update({ id: uid(8) });
          }}
        >
          <PlusIcon className="h-4 w-4" />
          Add Variant
        </Button>
      </div>
    </div>
  );
}
